/**
 * What `createFlightScene` builds before anything async: the delegating world
 * queries and the runner that flies on them.
 *
 * Lifted out of `sim-runner.ts` — which owns the tick and was at the file-size
 * gate's ceiling — because this is a different job. `sim-runner.ts` answers
 * *"what happens between frames"*; this answers *"which ground, which
 * buildings, and which of the three kinds of runner"*, once, at construction.
 */

import { DEFAULT_SURFACE, FLAT_GROUND, NO_SCENERY, type GroundQuery, type MissionSpec, type SceneryQuery } from "@red-opus/engine";
import { createEncounterRunner, createMissionRunner, type EncounterRunner, type MissionRunner } from "./sim-runner.ts";
import { FIRST_ENCOUNTER } from "./encounter-setup.ts";

export interface SceneRunnerOptions {
  readonly ground?: GroundQuery;
  readonly groundRef?: { current: GroundQuery };
  readonly sceneryRef?: { current: SceneryQuery };
  readonly mission?: MissionSpec;
  readonly runner?: EncounterRunner;
  /**
   * I12's ENHANCED SIMULATION, as the pilot has it set right now
   * (`flight/sim-settings.ts`). Stamped onto the mission's spec here and
   * nowhere else, so a hand-authored spec — a fixture, a test, a recorded tape
   * — keeps whatever it says for itself.
   *
   * Omitted or `false` and the spec is passed through untouched, which is what
   * every existing caller does and what keeps every generated mission's spec
   * byte-identical to the one it has always been.
   */
  readonly enhancedSimulation?: boolean;
}

export interface SceneRunner {
  groundRef: { current: GroundQuery };
  sceneryRef: { current: SceneryQuery };
  runner: EncounterRunner;
  missionRunner: MissionRunner | null;
  /** True only for the genuine instant dogfight — no `MissionSpec`, no caller-supplied runner. */
  isDogfight: boolean;
}

/**
 * The region's elevation query (`fly-region.ts`) only exists once the region
 * JSON has loaded, but the runner wants a `GroundQuery` now — so the runner
 * gets a delegating query whose target starts flat and is swapped for the
 * region's own in `watchInit`, strictly before the first tick. Zero sim ticks
 * ever see the swap happen, which is what keeps the flight (and a lockstep duel
 * peer) deterministic. A caller-injected `ground` is authoritative and is never
 * swapped out; a caller that built its own runner supplies `groundRef` so both
 * sides see the same ground (`flight-scene-api.ts`).
 *
 * The delegation is also why a mission whose player starts on his wheels has to
 * be **reseated** when the swap happens: until then the delegate answers flat
 * datum, so `createMissionRun` seats him on it (`MissionRunner.reseat`).
 */
export function sceneRunnerFor(options: SceneRunnerOptions): SceneRunner {
  const groundRef = options.groundRef ?? { current: options.ground ?? FLAT_GROUND };
  const ground: GroundQuery = {
    groundHeightAt: (x, z) => groundRef.current.groundHeightAt(x, z),
    surfaceAt: (x, z) => groundRef.current.surfaceAt?.(x, z) ?? DEFAULT_SURFACE,
  };
  // The buildings arrive with the region, exactly as the elevation does, so they
  // are swapped in through the same kind of ref and for the same reason: the
  // runner needs a query now and the region JSON has not loaded yet.
  const sceneryRef = options.sceneryRef ?? { current: NO_SCENERY };
  const scenery: SceneryQuery = {
    structureOnSegment: (from, to) => sceneryRef.current.structureOnSegment(from, to),
  };
  // The one place the pilot's ENHANCED SIMULATION bit becomes mission data. The
  // spec is what `createMissionRun` deploys ships from, and the *ground* it
  // deploys them over is the delegating query above — flat, and therefore dry,
  // until `watchInit` swaps the region's own in and `MissionRunner.reseat`
  // rebuilds the run. So a ship is put to sea by exactly the mechanism that
  // seats a wheels-down flight on real terrain, and for the same reason: the
  // sea is not knowable when the runner is built.
  const mission =
    options.mission && options.enhancedSimulation === true
      ? { ...options.mission, enhancedSimulation: true }
      : options.mission;
  const missionRunner = mission && !options.runner ? createMissionRunner(mission, ground, scenery) : null;
  return {
    groundRef,
    sceneryRef,
    missionRunner,
    isDogfight: !options.mission && !options.runner,
    runner: options.runner ?? missionRunner ?? createEncounterRunner(FIRST_ENCOUNTER, ground, scenery),
  };
}
